import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
  IconButton,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@mui/material";

import { AddIcon, DeleteIcon } from "../../../../2_ksbHub/icons/icons";

import { useEffect, useState } from "react";

import { useBszFinderContext } from "../../context/bszContext";
import bszService from "../../services";
import KsbDialogActions from "../../../../2_ksbHub/components/KsbDialogActions";

const ALL_GRADES = [1, 2, 3, 4, 5, 6];

export default function BszSubjectsAdminDialog({ open, onClose }) {
  const { subjects, loadSubjects } = useBszFinderContext();

  const [localSubjects, setLocalSubjects] = useState([]);
  const [originalSubjects, setOriginalSubjects] = useState([]);
  const [newSubject, setNewSubject] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setLocalSubjects(subjects || []);
      setOriginalSubjects(subjects || []);
      setNewSubject("");
    }
  }, [open, subjects]);

  const isNew = (s) => !originalSubjects.find((o) => o.id === s.id);

  const isChanged = (s) => {
    const original = originalSubjects.find((o) => o.id === s.id);

    if (!original) return false;

    const originalGrades = (original.grades || [])
      .map((g) => g.grade)
      .sort()
      .join(",");

    const currentGrades = (s.grades || [])
      .map((g) => g.grade)
      .sort()
      .join(",");

    return original.name !== s.name || originalGrades !== currentGrades;
  };

  const handleAdd = () => {
    const trimmed = newSubject.trim();

    if (!trimmed) return;

    setLocalSubjects((prev) => [
      ...prev,
      {
        id: Date.now(),
        name: trimmed,
        grades: ALL_GRADES.map((grade) => ({
          id: grade,
          grade,
        })),
      },
    ]);

    setNewSubject("");
  };

  const handleDelete = (id) => {
    setLocalSubjects((prev) => prev.filter((s) => s.id !== id));
  };

  const handleChange = (id, value) => {
    setLocalSubjects((prev) =>
      prev.map((s) => (s.id === id ? { ...s, name: value } : s)),
    );
  };

  const handleGradeToggle = (subjectId, grade) => {
    setLocalSubjects((prev) =>
      prev.map((subject) => {
        if (subject.id !== subjectId) {
          return subject;
        }

        const exists = (subject.grades || []).some((g) => g.grade === grade);

        if (exists) {
          return {
            ...subject,
            grades: subject.grades.filter((g) => g.grade !== grade),
          };
        }

        return {
          ...subject,
          grades: [
            ...(subject.grades || []),
            {
              id: grade,
              grade,
            },
          ],
        };
      }),
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const cleaned = localSubjects
        .map((s) => ({
          ...s,
          name: s.name.trim(),
        }))
        .filter((s) => s.name);

      await bszService.saveSubjects(cleaned);

      await loadSubjects();

      onClose();
    } catch (err) {
      console.error("saving subjects failed:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Fächer verwalten</DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {localSubjects.map((s) => {
            const newItem = isNew(s);
            const changedItem = isChanged(s);

            return (
              <Box
                key={s.id}
                sx={{
                  borderRadius: 1,
                  px: 1,
                  py: 1,
                  backgroundColor: newItem
                    ? "rgba(76,175,80,0.12)"
                    : changedItem
                      ? "rgba(255,193,7,0.15)"
                      : "transparent",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TextField
                    value={s.name}
                    onChange={(e) => handleChange(s.id, e.target.value)}
                    size="small"
                    fullWidth
                  />

                  {newItem && (
                    <Typography variant="caption" color="success.main">
                      neu
                    </Typography>
                  )}

                  {!newItem && changedItem && (
                    <Typography variant="caption" color="warning.main">
                      geändert
                    </Typography>
                  )}

                  <IconButton onClick={() => handleDelete(s.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    mt: 1,
                  }}
                >
                  {ALL_GRADES.map((grade) => (
                    <FormControlLabel
                      key={grade}
                      label={grade}
                      control={
                        <Checkbox
                          checked={(s.grades || []).some(
                            (g) => g.grade === grade,
                          )}
                          onChange={() => handleGradeToggle(s.id, grade)}
                        />
                      }
                    />
                  ))}
                </Box>
              </Box>
            );
          })}

          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <TextField
              value={newSubject}
              onChange={(e) => setNewSubject(e.target.value)}
              placeholder="Neues Fach..."
              size="small"
              fullWidth
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAdd();
                }
              }}
            />

            <IconButton onClick={handleAdd}>
              <AddIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogContent>

      <KsbDialogActions
        onCancel={onClose}
        onConfirm={handleSave}
        confirmLabel="Speichern"
        confirmDisabled={saving}
        confirmLoading={saving}
      />
    </Dialog>
  );
}
